import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import tw from "twin.macro";

import {
  Container,
  Hyperlink,
  Paragraph,
  Heading,
  SubHeading
} from "../../components/section";
import SEO from "../../components/seo";
import Layout from "../../components/layout";
import AppLink from "../../components/app-link";

const Main = tw.main`container mx-auto`;
const SideBySide = tw.div`flex flex-col lg:flex-row space-y-8 lg:space-y-0 lg:space-x-8`;
const Aside = tw.aside``;
const Content = tw.div`flex-1`;
const Locations = tw.div`lg:max-w-sm p-4`;
const Details = tw.ul`bg-gray-100 lg:max-w-sm rounded shadow-inner p-4`;
const DetailsItem = tw.li``;
const DetailsLabel = tw.span`font-bold`;
const LoadingMessage = tw.div``;

const Template = ({ location }) => {
  const [loading, setLoading] = useState(true);
  const [trial, setTrial] = useState({});

  const params = new URLSearchParams(location.search)
  const nctid = params.get("nctid");

  const query = `https://clinicaltrials.gov/api/v2/studies?postFilter.ids=${nctid}&format=json`;

  useEffect(() => {
    fetch(query)
      .then(res => res.json())
      .then(json => {
        const study = json.studies[0];
        const {
          identificationModule,
          statusModule,
          sponsorCollaboratorsModule,
          descriptionModule,
          eligibilityModule,
          contactsLocationsModule,
          outcomesModule
        } = study.protocolSection;
        const { responsibleParty } = sponsorCollaboratorsModule;
        const { overallOfficialList, locationList } = contactsLocationsModule;
        const { overallOfficial } = overallOfficialList || {};
        const {
          primaryOutcomeList,
          secondaryOutcomeList
        } = outcomesModule || {};
        const { primaryOutcome } = primaryOutcomeList || {};
        const { secondaryOutcome } = secondaryOutcomeList || {};
        setTrial({
          briefTitle: identificationModule.briefTitle,
          dcpid: identificationModule.secondaryIdInfos.filter(
            meta =>
              meta.SecondaryIdDomain == "DCP" &&
              meta.SecondaryIdType == "Other Identifier"
          )[0]?.secondaryId,
          overallStatus: statusModule.overallStatus,
          startDate: statusModule?.startDateStruct?.date,
          investigators: overallOfficial
            ? overallOfficial.map(official => ({
                name: official.overallOfficialName,
                role: official.overallOfficialRole,
                affiliation: official.overallOfficialAffiliation
              }))
            : [],
          investigator: {
            type: responsibleParty.responsiblePartyType,
            title: responsibleParty.responsiblePartyInvestigatorTitle,
            name: responsibleParty.responsiblePartyInvestigatorFullName,
            affiliation:
              responsibleParty.responsiblePartyInvestigatorAffiliation
          },
          sponsor: sponsorCollaboratorsModule.leadSponsor.name,
          description: descriptionModule.briefSummary,
          eligibility: {
            criteria: eligibilityModule.eligibilityCriteria,
            population: eligibilityModule.studyPopulation
          },
          primaryOutcomes: primaryOutcome,
          secondaryOutcomes: secondaryOutcome,
          locations:
            locationList?.location?.filter(
              l => l.locationStatus == "Recruiting"
            ) || []
        });
        setLoading(false);
      })
      .catch(err => console.error("Error getting trial information: ", err));
  }, [query]);
  return (
    <Layout>
      <Main>
        <Container>
          {loading ? (
            <LoadingMessage>Loading...</LoadingMessage>
          ) : (
            <>
              <AppLink to="/trials">Back to Trials</AppLink>
              <Heading tw="text-left">{trial.briefTitle}</Heading>
              <SEO title={trial.briefTitle} />
              <SideBySide>
                <Aside>
                  <Details>
                    <DetailsItem>
                      <DetailsLabel>ClinicalTrials.gov ID:</DetailsLabel>{" "}
                      {nctid}
                    </DetailsItem>
                    {trial.dcpid && (
                      <DetailsItem>
                        <DetailsLabel>DCP ID:</DetailsLabel> {trial.dcpid}
                      </DetailsItem>
                    )}
                    <DetailsItem>
                      <DetailsLabel>Status:</DetailsLabel> <span tw="capitalize">{trial.overallStatus.replaceAll("_", " ").toLowerCase()}</span>
                    </DetailsItem>
                    <DetailsItem>
                      <DetailsLabel>Start Date:</DetailsLabel> {trial.startDate}
                    </DetailsItem>
                    {trial.investigator.type == "Investigator" && (
                      <DetailsItem>
                        <DetailsLabel>{trial.investigator.title}:</DetailsLabel>{" "}
                        {trial.investigator.name} (
                        {trial.investigator.affiliation})
                      </DetailsItem>
                    )}
                    {trial.investigators.map(investigator => (
                      <DetailsItem key={investigator.name}>
                        <DetailsLabel>{investigator.role}: </DetailsLabel>
                        {investigator.name} ({investigator.affiliation})
                      </DetailsItem>
                    ))}
                    <DetailsItem>
                      <DetailsLabel>Sponsor:</DetailsLabel> {trial.sponsor}
                    </DetailsItem>
                    <DetailsItem tw="border-t border-gray-300 pt-2 mt-2 text-xs ">
                      Get more information about this trial at{" "}
                      <Hyperlink
                        href={`https://www.clinicaltrials.gov/ct2/show/${nctid}`}
                      >
                        ClinicalTrials.gov
                      </Hyperlink>
                    </DetailsItem>
                  </Details>
                  {trial.locations.length > 0 &&
                  <Locations>
                    <p tw="mt-4 font-bold">Participating Sites</p>
                    {trial.locations.map(site => (
                      <div style={{ marginLeft: "1rem", textIndent: "-1rem" }} key={site.locationFacility}>
                        {site.LocationFacility}
                      </div>
                    ))}
                  </Locations>
                  }
                </Aside>
                <Content>
                  {trial.description && (
                    <>
                      <SubHeading tw="border-b">Description</SubHeading>
                      <Paragraph tw="whitespace-pre-line">
                        {trial.description}
                      </Paragraph>
                    </>
                  )}
                  {trial.eligibility.criteria && (
                    <>
                      <SubHeading tw="border-b">
                        Eligibility Criteria
                      </SubHeading>
                      {trial.eligibility.criteria.split("\n\n").map((s, i) =>
                        s.includes("\n") ? (
                          <ul key={`criteria-${i}`}>
                            {s.split("\n").map((item, idx) => (
                              <li key={`item-${idx}`}>{item}</li>
                            ))}
                          </ul>
                        ) : (
                          <p key={`criteria-${i}`} tw="my-4">
                            {s}
                          </p>
                        )
                      )}
                    </>
                  )}
                  {trial.eligibility.population && (
                    <>
                      <SubHeading tw="border-b">Population</SubHeading>
                      <Paragraph>{trial.eligibility.population}</Paragraph>
                    </>
                  )}
                  {trial.primaryOutcomes && (
                    <>
                      <SubHeading tw="border-b">Primary Outcomes</SubHeading>
                      {trial.primaryOutcomes.map((outcome, i) => (
                        <div key={`outcome-${i}`}>
                          <Paragraph tw="font-bold">
                            {outcome.PrimaryOutcomeMeasure}
                          </Paragraph>
                          <Paragraph>
                            {outcome.PrimaryOutcomeDescription}
                          </Paragraph>
                          <Paragraph>
                            Time Frame: {outcome.PrimaryOutcomeTimeFrame}
                          </Paragraph>
                        </div>
                      ))}
                    </>
                  )}
                  {trial.secondaryOutcomes && (
                    <>
                      <SubHeading tw="border-b">Secondary Outcomes</SubHeading>
                      {trial.secondaryOutcomes.map((outcome, i) => (
                        <div key={`outcome-${i}`}>
                          <Paragraph tw="font-bold">
                            {outcome.SecondaryOutcomeMeasure}
                          </Paragraph>
                          <Paragraph>
                            {outcome.SecondaryOutcomeDescription}
                          </Paragraph>
                          <Paragraph>
                            Time Frame: {outcome.SecondaryOutcomeTimeFrame}
                          </Paragraph>
                        </div>
                      ))}
                    </>
                  )}
                  <div tw="text-2xl font-bold mt-8 pt-4 border-t">
                    More information about this trial is available at{" "}
                    <Hyperlink
                      href={`https://www.clinicaltrials.gov/ct2/show/${nctid}`}
                    >
                      ClinicalTrials.gov
                    </Hyperlink>
                  </div>
                </Content>
              </SideBySide>
            </>
          )}
        </Container>
      </Main>
    </Layout>
  );
};

Template.propTypes = {
  location: PropTypes.object
};

export default Template;
